<script lang="ts">
    // Jet/Svelte
    import { createEventDispatcher, onMount } from 'svelte';
    import { getJet } from '~/jet';

    // Components
    import MobileNowPlaying from '~/components/structure/Players/components/MobileNowPlaying/MobileNowPlaying.svelte';
    import DesktopNowPlaying from '~/components/structure/Players/components/DesktopNowPlaying/DesktopNowPlaying.svelte';

    // Actions
    import {
        makePausePlaybackAction,
        makeResumePlaybackAction,
    } from '~/actions/playback-actions';

    // Stores
    import {
        DEFAULT_NOW_PLAYING_VIEW,
        NowPlayingView,
        fullScreenPlayerState,
    } from '~/stores/full-screen-player';

    // Utilities
    import { getPlayback } from '~/utils/playback';

    // Shared Utilities
    import { mediaQueries } from '@amp/web-app-components/src/stores/media-query';
    import ResizeDetector from '@amp/web-app-components/src/components/helpers/ResizeDetector.svelte';

    const dispatch = createEventDispatcher();
    const { queue } = getPlayback();
    const jet = getJet();

    export let modalTriggerElement: HTMLElement;
    export let isFullScreenActive: boolean;
    export let translateFn: (key: string) => string;

    // TODO: Determine aria labels for both mobile and desktop views
    const ariaLabel = '';

    $: nowPlayingView = $fullScreenPlayerState.nowPlayingView;

    $: isMobile = $mediaQueries === 'xsmall' || $mediaQueries === 'small';

    $: isPaused = $queue.derivedStates.isPaused;

    $: playOrPauseAction = isPaused
        ? makeResumePlaybackAction()
        : makePausePlaybackAction();

    function playOrPauseItem() {
        jet.perform(playOrPauseAction);
    }

    const toggleView = (view: NowPlayingView): void => {
        if (nowPlayingView === view) {
            fullScreenPlayerState.setNowPlayingView(DEFAULT_NOW_PLAYING_VIEW);
        } else {
            fullScreenPlayerState.setNowPlayingView(view);
        }
    };

    // Flag for whether window is resizing, used to determine whether sliding modal
    // animation should be enabled when mounting and unmounting <MobileNowPlaying>
    let isResizing = false;

    const handleResizeUpdate = (e: CustomEvent<{ isResizing: boolean }>) => {
        isResizing = e.detail.isResizing;
    };

    // When browser Back button is clicked, dispatch an event that
    // will close the full screen player in the parent component
    function handlePopState(): void {
        dispatch('close');
    }

    onMount(() => {
        window?.addEventListener('popstate', handlePopState);

        return () => {
            window?.removeEventListener('popstate', handlePopState);
        };
    });
</script>

<ResizeDetector on:resizeUpdate={handleResizeUpdate} />

{#if isMobile}
    <MobileNowPlaying
        {modalTriggerElement}
        {translateFn}
        {ariaLabel}
        disableSlide={isResizing}
        on:close
        on:artworkClick={playOrPauseItem}
        on:toggleView={(e) => toggleView(e.detail)}
    />
{:else}
    <DesktopNowPlaying
        {modalTriggerElement}
        {isFullScreenActive}
        {translateFn}
        {ariaLabel}
        on:close
        on:artworkClick={playOrPauseItem}
        on:toggleView={(e) => toggleView(e.detail)}
    />
{/if}
